/**
 * Utilitários para manipulação de strings
 */

/**
 * Remove todos os caracteres não numéricos de uma string
 * @param value String original
 * @returns String contendo apenas números
 */
export const removeNonNumeric = (value: string): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

/** Telefone BR (proposta comercial / QSA): DDD + fixo (10) ou celular (11) — só dígitos. */
export const BRAZIL_PHONE_MAX_DIGITS = 11;

/**
 * Normaliza digitação/cola de telefone BR para dígitos nacionais (DDD + 8 ou 9).
 * Remove DDI 55 somente quando o restante já é um número nacional (10 ou 11 dígitos).
 * Não remove 55 de números com 11 dígitos — DDD 55 (RS) é válido.
 */
export function toBrazilNationalPhoneDigits(value: string): string {
  const digits = removeNonNumeric(value);
  if (digits.startsWith('55')) {
    const national = digits.slice(2);
    if (national.length >= 10) {
      return national.slice(0, BRAZIL_PHONE_MAX_DIGITS);
    }
  }
  return digits.slice(0, BRAZIL_PHONE_MAX_DIGITS);
}

/**
 * Entrada de telefone do representante legal: só dígitos, no máximo 11 (ex.: 41984028564).
 */
export function normalizeRepresentativePhoneInput(value: string): string {
  return toBrazilNationalPhoneDigits(value);
}

/**
 * Normaliza um CEP removendo caracteres especiais e espaços
 * @param cep CEP no formato original (com ou sem máscara)
 * @returns CEP normalizado com apenas dígitos
 * @throws Error se o CEP for inválido ou vazio
 */
export const normalizeCep = (cep: string): string => {
  if (!cep || cep.trim() === '') {
    throw new Error('CEP é obrigatório');
  }

  // Remove todos os caracteres não numéricos
  const cepDigitsOnly = removeNonNumeric(cep);

  // Verifica se tem exatamente 8 dígitos
  if (cepDigitsOnly.length !== 8) {
    throw new Error('CEP deve conter exatamente 8 dígitos');
  }

  return cepDigitsOnly;
};

/**
 * Formata um CEP com máscara (XXXXX-XXX)
 * @param cep CEP com 8 dígitos
 * @returns CEP formatado
 */
export const formatCep = (cep: string): string => {
  const digits = removeNonNumeric(cep);
  if (digits.length === 8) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return cep; // Retorna original se não conseguir formatar
};

/**
 * Formata CEP para exibição em input (ex.: ao carregar contrato ou cliente).
 * Retorna string vazia para CEP vazio ou inválido (00000-000, 00000000).
 */
export const formatCepForInput = (cep: string | undefined): string => {
  if (!cep || cep.trim() === '' || cep === '00000-000' || cep === '00000000' || removeNonNumeric(cep) === '00000000') {
    return '';
  }
  const cepLimpo = removeNonNumeric(cep);
  if (cepLimpo.length === 8) {
    return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5)}`;
  }
  return cepLimpo;
};

/**
 * Normaliza um CPF removendo caracteres especiais e espaços
 * @param cpf CPF no formato original (com ou sem máscara)
 * @returns CPF normalizado com apenas dígitos
 * @throws Error se o CPF for inválido
 */
export const normalizeCpf = (cpf: string): string => {
  if (!cpf || cpf.trim() === '') {
    throw new Error('CPF é obrigatório');
  }

  const cpfDigitsOnly = removeNonNumeric(cpf);

  if (cpfDigitsOnly.length !== 11) {
    throw new Error('CPF deve conter exatamente 11 dígitos');
  }

  return cpfDigitsOnly;
};

/**
 * Formata um CPF com máscara (XXX.XXX.XXX-XX)
 * @param cpf CPF com 11 dígitos
 * @returns CPF formatado
 */
export const formatCpf = (cpf: string): string => {
  const digits = removeNonNumeric(cpf);
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return cpf;
};

/**
 * Normaliza um CNPJ removendo caracteres especiais e espaços
 * @param cnpj CNPJ no formato original (com ou sem máscara)
 * @returns CNPJ normalizado com apenas dígitos
 * @throws Error se o CNPJ for inválido
 */
export const normalizeCnpj = (cnpj: string): string => {
  if (!cnpj || cnpj.trim() === '') {
    throw new Error('CNPJ é obrigatório');
  }

  const cnpjDigitsOnly = removeNonNumeric(cnpj);

  if (cnpjDigitsOnly.length !== 14) {
    throw new Error('CNPJ deve conter exatamente 14 dígitos');
  }

  return cnpjDigitsOnly;
};

/**
 * Formata um CNPJ com máscara (XX.XXX.XXX/XXXX-XX)
 * @param cnpj CNPJ com 14 dígitos
 * @returns CNPJ formatado
 */
export const formatCnpj = (cnpj: string): string => {
  const digits = removeNonNumeric(cnpj);
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return cnpj;
};

/**
 * Remove espaços em branco do início e fim da string
 * @param value String original
 * @returns String sem espaços em branco nas extremidades
 */
export const trimString = (value: string): string => {
  if (!value) return '';
  return value.trim();
};

/**
 * Verifica se uma string está vazia ou contém apenas espaços
 * @param value String a ser verificada
 * @returns true se estiver vazia ou apenas espaços
 */
export const isEmptyOrWhitespace = (value: string): boolean => {
  return !value || value.trim() === '';
};

/**
 * Capitaliza a primeira letra de cada palavra
 * @param value String original
 * @returns String com primeiras letras capitalizadas
 */
export const capitalizeWords = (value: string): string => {
  if (!value) return '';
  return value.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Limita o tamanho de uma string
 * @param value String original
 * @param maxLength Tamanho máximo
 * @returns String limitada
 */
export const limitLength = (value: string, maxLength: number): string => {
  if (!value || value.length <= maxLength) return value;
  return value.substring(0, maxLength);
};

/**
 * Limpa um CPF removendo caracteres não numéricos e limitando a 11 dígitos
 * @param value String original (pode conter formatação)
 * @returns CPF limpo com apenas números (máximo 11 dígitos)
 */
export const cleanCpf = (value: string): string => {
  if (!value) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = value.replace(/\D/g, '');
  // Retorna apenas os primeiros 11 dígitos
  return cleaned.substring(0, 11);
};

/**
 * Formata um CPF com máscara progressiva durante a digitação
 * @param cpf CPF limpo (apenas números)
 * @returns CPF formatado progressivamente
 */
export const formatCpfProgressive = (cpf: string): string => {
  const cleaned = cleanCpf(cpf);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
};

/**
 * Verifica se um CPF tem o tamanho correto (11 dígitos)
 * @param cpf CPF a ser validado
 * @returns true se tiver 11 dígitos
 */
export const isValidCpfLength = (cpf: string): boolean => {
  return cleanCpf(cpf).length === 11;
};

/**
 * CPF completo (11 dígitos) a partir do CpfCnpj do partner.
 * Retorna '' quando a Receita/CNPJ WS manda valor mascarado (ex.: ***886437**).
 */
export const resolveCompleteCpfFromPartnerSource = (
  cpfCnpj: string | null | undefined
): string => {
  const cleaned = cleanCpf(cpfCnpj || '');
  return cleaned.length === 11 ? cleaned : '';
};

/** Indica CPF mascarado ou incompleto vindo do QSA (não pode ir direto para o contrato). */
export const isPartnerCpfMaskedOrIncomplete = (
  cpfCnpj: string | null | undefined
): boolean => {
  if (!cpfCnpj?.trim()) return true;
  if (/[*xX]/.test(cpfCnpj)) return true;
  return cleanCpf(cpfCnpj).length !== 11;
};

/**
 * Limpa um CNPJ removendo caracteres não numéricos e limitando a 14 dígitos
 * @param value String original (pode conter formatação)
 * @returns CNPJ limpo com apenas números (máximo 14 dígitos)
 */
export const cleanCnpj = (value: string): string => {
  if (!value) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = value.replace(/\D/g, '');
  // Retorna apenas os primeiros 14 dígitos
  return cleaned.substring(0, 14);
};

/**
 * Formata um CNPJ com máscara progressiva durante a digitação
 * @param cnpj CNPJ limpo (apenas números)
 * @returns CNPJ formatado progressivamente
 */
export const formatCnpjProgressive = (cnpj: string): string => {
  const cleaned = cleanCnpj(cnpj);
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  if (cleaned.length <= 12) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
};

/**
 * Verifica se um CNPJ tem o tamanho correto (14 dígitos)
 * @param cnpj CNPJ a ser validado
 * @returns true se tiver 14 dígitos
 */
export const isValidCnpjLength = (cnpj: string): boolean => {
  return cleanCnpj(cnpj).length === 14;
};

/**
 * Limpa um CEP removendo caracteres não numéricos e limitando a 8 dígitos
 * @param value String original (pode conter formatação)
 * @returns CEP limpo com apenas números (máximo 8 dígitos)
 */
export const cleanCep = (value: string): string => {
  if (!value) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = value.replace(/\D/g, '');
  // Retorna apenas os primeiros 8 dígitos
  return cleaned.substring(0, 8);
};

/**
 * Formata um CEP com máscara progressiva durante a digitação
 * @param cep CEP limpo (apenas números)
 * @returns CEP formatado progressivamente
 */
export const formatCepProgressive = (cep: string): string => {
  const cleaned = cleanCep(cep);
  if (cleaned.length <= 5) return cleaned;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
};

/**
 * Verifica se um CEP tem o tamanho correto (8 dígitos)
 * @param cep CEP a ser validado
 * @returns true se tiver 8 dígitos
 */
export const isValidCepLength = (cep: string): boolean => {
  return cleanCep(cep).length === 8;
};

/**
 * Limpa um telefone removendo caracteres não numéricos
 * @param value String original (pode conter formatação)
 * @returns Telefone limpo com apenas números
 */
export const cleanPhone = (value: string): string => {
  if (!value) return '';
  // Remove todos os caracteres não numéricos
  return value.replace(/\D/g, '');
};

/**
 * Extrai DDD de um telefone no formato nacional BR (10 ou 11 dígitos).
 * Com 9 dígitos devolve `ddd` vazio (celular local 9XXXXXXXX não deve ser partido — coincide com prefixo do DDD).
 */
export const extractDddFromPhone = (phoneNumber: string): { ddd: string; number: string } => {
  const cleaned = cleanPhone(phoneNumber);
  
  // Se tem 11 dígitos (DDD + 9 dígitos) - formato padrão brasileiro
  if (cleaned.length === 11) {
    const ddd = cleaned.substring(0, 2);
    const number = cleaned.substring(2);
    return { ddd, number };
  }
  
  // Se tem 10 dígitos (DDD + 8 dígitos) - telefone fixo
  if (cleaned.length === 10) {
    const ddd = cleaned.substring(0, 2);
    const number = cleaned.substring(2);
    return { ddd, number };
  }

  // 9 dígitos: celular local (9XXXXXXXX) — não partir em DDD+número (ex.: DDD 98 e número 98765…
  // seria interpretado errado como DDD 98 + local 765…). Só tratar como número local.
  return { ddd: '', number: cleaned };
};

/**
 * Monta o telefone nacional (apenas dígitos) para exibição/validação quando DDD e número
 * vêm em campos separados. Só trata `numeroArmazenado` como já completo (DDD+local) se tiver
 * 10 ou 11 dígitos e começar com o DDD — evita confundir local que casualmente inicia com o mesmo par de dígitos.
 */
export function mergeDddAndLocalPhoneDigits(ddd: string, numeroArmazenado: string): string {
  const d = cleanPhone(ddd);
  const n = cleanPhone(numeroArmazenado);
  if (!n) return '';
  if (d && n.startsWith(d) && (n.length === 10 || n.length === 11)) {
    return n;
  }
  if (d) {
    return d + n;
  }
  return n;
}

/**
 * Parte local (sem DDD) para armazenamento/API quando o DDD vai em campo separado.
 * Remove o prefixo DDD só se o valor do campo for telefone nacional completo (10 ou 11 dígitos).
 */
export function renegociacaoLocalDigitsFromFormField(ddd: string, numeroCampo: string): string {
  const d = cleanPhone(ddd);
  const n = cleanPhone(numeroCampo);
  if (!n) return '';
  if (d && n.startsWith(d) && (n.length === 10 || n.length === 11)) {
    return n.slice(d.length);
  }
  return n;
}

export interface RenegociacaoNumeroFieldUpdate {
  ddd?: string;
  numero: string;
}

/**
 * Trata digitação/cola no campo Número (Renegociação) com DDD em coluna separada.
 * Só preenche o DDD quando ele ainda está vazio; se já foi escolhido, não altera.
 */
export function parseRenegociacaoNumeroFieldInput(
  currentDdd: string,
  rawInput: string
): RenegociacaoNumeroFieldUpdate {
  const national = toBrazilNationalPhoneDigits(rawInput);
  if (national.length >= 10) {
    const { ddd: extractedDdd, number } = extractDddFromPhone(national);
    if (extractedDdd) {
      const local = number.slice(0, 9);
      if (currentDdd) {
        return { numero: local };
      }
      return { ddd: extractedDdd, numero: local };
    }
  }
  const local = currentDdd
    ? renegociacaoLocalDigitsFromFormField(currentDdd, national)
    : national;
  return { numero: local.slice(0, 9) };
}

/**
 * Formata um telefone com máscara progressiva
 * @param phone Telefone limpo (apenas números)
 * @returns Telefone formatado progressivamente
 */
export const formatPhoneProgressive = (phone: string): string => {
  const cleaned = cleanPhone(phone);
  
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  if (cleaned.length <= 11) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
};

/**
 * Valida se um telefone tem formato válido (10 ou 11 dígitos)
 * @param phone Telefone a ser validado
 * @returns true se tiver formato válido
 */
export const isValidPhoneFormat = (phone: string): boolean => {
  const cleaned = cleanPhone(phone);
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Valida se um DDD é válido (2 dígitos)
 * @param ddd DDD a ser validado
 * @returns true se tiver 2 dígitos
 */
export const isValidDdd = (ddd: string): boolean => {
  const cleaned = cleanPhone(ddd);
  return cleaned.length === 2;
};

/**
 * Limpa um CCID removendo caracteres não numéricos e limitando a 20 dígitos
 * @param value String original (pode conter formatação)
 * @returns CCID limpo com apenas números (máximo 20 dígitos)
 */
export const cleanCcid = (value: string): string => {
  if (!value) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = value.replace(/\D/g, '');
  // Retorna apenas os primeiros 20 dígitos
  return cleaned.substring(0, 20);
};

/**
 * Valida se um CCID tem o tamanho correto (20 dígitos)
 * @param ccid CCID a ser validado
 * @returns true se tiver 20 dígitos
 */
export const isValidCcidLength = (ccid: string): boolean => {
  return cleanCcid(ccid).length === 20;
};